import React, { useState, useEffect } from 'react';
import AuthenticatedNavBar from '../../components/ui/AuthenticatedNavBar';
import ArticleParametersPanel from './components/ArticleParametersPanel';
import ArticleEditorPanel from './components/ArticleEditorPanel';
import GenerationStatusCard from './components/GenerationStatusCard';
import UpgradeModal from '../../components/ui/UpgradeModal';

const NewsGeneration = () => {
  const [user, setUser] = useState(null);
  const [generatedArticle, setGeneratedArticle] = useState(null);
  const [generationStatus, setGenerationStatus] = useState('idle');
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationMessage, setGenerationMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const API_URL = import.meta.env?.VITE_SERVER_URL || 'http://localhost:4000';

  // Load real user for navbar
  useEffect(() => {
    const loadUser = async () => {
      try {
        const stored = localStorage.getItem('user');
        if (stored) {
          setUser(JSON.parse(stored));
        }
        const res = await fetch(`${API_URL}/api/user/data`, { credentials: 'include' });
        const data = await res.json();
        if (data?.success) {
          const merged = {
            ...JSON.parse(localStorage.getItem('user') || '{}'),
            name: data?.userData?.name,
            email: data?.userData?.email,
            isVerified: data?.userData?.isAccountVerified ?? false
          };
          setUser(merged);
          localStorage.setItem('user', JSON.stringify(merged));
        }
      } catch (_) {}
    };
    loadUser();
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      // ignore network errors on logout
    } finally {
      localStorage.removeItem('ddf_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  };

  const simulateProgress = () => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 15 + 5;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        setTimeout(() => {
          setGenerationStatus('success');
          setGenerationMessage('Article generated successfully!');
          setIsLoading(false);
        }, 500);
      }
      setGenerationProgress(progress);
    }, 800);

    return interval;
  };

  const handleGenerateArticle = async (parameters) => {
    setIsLoading(true);
    setGenerationStatus('processing');
    setGenerationProgress(0);
    setGenerationMessage('Analyzing topic and gathering information...');

    const progressInterval = simulateProgress();

    try {
      // Send all form parameters (topic, style, length, tone, sources) to Node.js server
      // which proxies to FastAPI backend for AI processing
      const res = await fetch(`${API_URL}/api/agents/news`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          topic: parameters?.topic,
          style: parameters?.style || 'news',
          length: parameters?.length || 'medium',
          tone: parameters?.tone || 'neutral',
          sources: parameters?.sources || ''  // Now properly connected to form input
        })
      });
      const data = await res.json();
      
      // Handle usage limit exceeded (429) or specific error responses
      if (res.status === 429 || data?.upgradeRequired) {
        clearInterval(progressInterval);
        setIsLoading(false);
        setGenerationStatus('limit_reached');
        setGenerationMessage(data?.message || 'Usage limit reached');
        setShowUpgradeModal(true);
        return;
      }
      
      if (!res.ok) {
        throw new Error(data?.message || `Request failed (${res.status})`);
      }
      
      if (data?.error) throw new Error(data?.error);

      // Shape already matches UI: { id, title, content, parameters, createdAt, lastModified, wordCount, status }
      setGeneratedArticle(data);
      // finish progress immediately on success
      setGenerationProgress(100);
      setGenerationStatus('success');
      setGenerationMessage('Article generated successfully!');
      setIsLoading(false);
      clearInterval(progressInterval);
    } catch (error) {
      console.error('Generation failed:', error);
      setGenerationStatus('error');
      setGenerationMessage('Failed to generate article. Please try again.');
      setIsLoading(false);
      clearInterval(progressInterval);
    }
  };

  const handleSaveArticle = (updatedArticle) => {
    setGeneratedArticle(updatedArticle);
    // Here you would typically save to backend
    console.log('Article saved:', updatedArticle);
  };

  const handleClearArticle = () => {
    setGeneratedArticle(null);
    setGenerationStatus('idle');
    setGenerationProgress(0);
    setGenerationMessage('');
  };

  const handleCancelGeneration = () => {
    setIsLoading(false);
    setGenerationStatus('idle');
    setGenerationProgress(0);
    setGenerationMessage('');
  };

  return (
    <div className="min-h-screen bg-background">
      <AuthenticatedNavBar user={user} onLogout={handleLogout} />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">AI</span>
              </div>
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">
                  News Generation
                </h1>
                <p className="text-muted-foreground">
                  Create AI-powered articles with intelligent assistance and real-time editing
                </p>
              </div>
            </div>
            
            <GenerationStatusCard
              status={generationStatus}
              progress={generationProgress}
              message={generationMessage}
              onCancel={handleCancelGeneration}
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-[calc(100vh-280px)] min-h-[600px]">
            {/* Left Panel - Article Parameters */}
            <div className="order-1 lg:order-1">
              <ArticleParametersPanel
                onGenerate={handleGenerateArticle}
                loading={isLoading}
              />
            </div>

            {/* Right Panel - Article Editor */}
            <div className="order-2 lg:order-2">
              <ArticleEditorPanel
                article={generatedArticle}
                onSave={handleSaveArticle}
                onClear={handleClearArticle}
                loading={isLoading}
              />
            </div>
          </div>

          {/* Mobile Stack Layout for smaller screens */}
          <div className="lg:hidden mt-8">
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground">
                💡 <strong>Tip:</strong> For the best experience, use a larger screen or rotate your device to landscape mode.
              </p>
            </div>
          </div>
        </div>
      </main>
      
      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="news generation"
        usageInfo={{
          usage: 5, // This will be dynamic based on user data
          limit: 5
        }}
      />
    </div>
  );
};

export default NewsGeneration;